import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/joy/Autocomplete';
import AutocompleteOption from '@mui/joy/AutocompleteOption';
import CircularProgress from '@mui/joy/CircularProgress';
import Typography from '@mui/joy/Typography';
import axiosConfig from '../util/axiosConfig';

let cachedSpecializations = null;

/**
 * Searchable specialization dropdown for UG/PG fields.
 * Stores short_name in form state; displays full name in dropdown.
 * freeSolo so existing values not in the list are preserved as-is.
 *
 * Props:
 *   value    {string} — short_name currently stored (e.g. "ECE")
 *   onChange {(shortName: string) => void}
 *   sx       {object}
 *   disabled {boolean}
 *   required {boolean}
 *   error    {boolean}
 */
export default function SpecializationSearch({ value = '', onChange, sx, disabled, required, error }) {
    const [options, setOptions] = useState(cachedSpecializations || []);
    const [loading, setLoading] = useState(!cachedSpecializations);

    useEffect(() => {
        if (cachedSpecializations) return;
        axiosConfig.get('/students/specializations')
            .then(res => {
                if (res.data?.success) {
                    cachedSpecializations = res.data.data;
                    setOptions(cachedSpecializations);
                }
            })
            .catch(err => console.error('[Spec] fetch failed:', err))
            .finally(() => setLoading(false));
    }, []);

    const selectedOption = options.find(o => o.short_name === value) || null;

    const handleChange = (_, newValue) => {
        if (!newValue) {
            onChange('');
        } else if (typeof newValue === 'string') {
            onChange(newValue);
        } else {
            onChange(newValue.short_name);
        }
    };

    return (
        <Autocomplete
            freeSolo
            loading={loading}
            options={options}
            value={selectedOption || value}
            onChange={handleChange}
            onInputChange={(_, inputVal, reason) => {
                if (reason === 'clear') onChange('');
            }}
            getOptionLabel={opt =>
                typeof opt === 'string' ? opt : `${opt.short_name} — ${opt.name}`
            }
            groupBy={opt => (typeof opt === 'string' ? '' : opt.category)}
            isOptionEqualToValue={(opt, val) =>
                typeof val === 'string'
                    ? opt.short_name === val
                    : opt.short_name === val?.short_name
            }
            renderOption={(props, opt) => (
                <AutocompleteOption {...props} key={opt.short_name}>
                    <Typography level="body-sm" sx={{ fontWeight: 600, mr: 0.5 }}>
                        {opt.short_name}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: '#6e7072' }}>
                        — {opt.name}
                    </Typography>
                </AutocompleteOption>
            )}
            filterOptions={(opts, { inputValue }) => {
                const q = inputValue.toLowerCase().trim();
                if (!q) return opts;
                return opts.filter(o =>
                    o.short_name.toLowerCase().includes(q) ||
                    o.name.toLowerCase().includes(q) ||
                    o.category.toLowerCase().includes(q)
                );
            }}
            endDecorator={loading ? <CircularProgress size="sm" /> : null}
            placeholder="Search by name or code (e.g. ECE, Electronics)"
            disabled={disabled}
            required={required}
            sx={{
                '--Input-radius': '8px',
                '--Input-focusedHighlight': error ? '#ef4444' : '#eb7a24',
                width: '100%',
                fontSize: '0.875rem',
                bgcolor: '#fff',
                ...(error && { boxShadow: 'inset 0 0 0 1.5px #ef4444' }),
                ...sx,
            }}
        />
    );
}
